import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { AdPositionEnum, Blog } from "../../../client/api";
import { ClavaContext } from "../../../config/contexts";
import { pathNews } from "../../../config/routes";
import { translate } from "../../../config/translator";
import NewsDetailModal from "../NewsDetailModal";
import ProgressIndicator from "../ProgressIndicator";
import NewsItem from "./NewsItem";
import { AdSlot } from "../AdSlot";

const NEWS_PER_STEP = 2;
const AUTO_PLAY_INTERVAL = 5000;
const SCROLL_RESET_TIMEOUT = 3000;

type NewsPanelProps = {
  news: Blog[];
  loading?: boolean;
};

const NewsPanel: React.FC<NewsPanelProps> = ({ news, loading = false }) => {
  const { l } = useContext(ClavaContext);
  const [selectedNews, setSelectedNews] = useState<Blog | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const carouselRef = useRef<HTMLDivElement>(null);
  const autoPlayIntervalRef = useRef<number | null>(null);
  const isAutoScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const userScrollingRef = useRef(false);

  const totalSteps = useMemo(
    () => Math.ceil(news.length / NEWS_PER_STEP),
    [news.length]
  );
  
  const isOverlayOpen = useCallback(() => {
    const dropdownOverlay = document.getElementById("clava-dropdown-overlay");
    const bodyScrollLocked = document.body.style.overflow === "hidden";
    return !!(dropdownOverlay || bodyScrollLocked);
  }, []);

  useEffect(() => {
    if (totalSteps <= 1) {
      return undefined;
    }

    const startAutoPlay = () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
      autoPlayIntervalRef.current = window.setInterval(() => {
        // Don't auto-scroll if user is scrolling, already scrolling, or overlay is open
        if (!userScrollingRef.current && !isAutoScrollingRef.current && !isOverlayOpen()) {
          setCurrentStep((prev) => (prev + 1) % totalSteps);
        }
      }, AUTO_PLAY_INTERVAL);
    };

    startAutoPlay();

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [totalSteps, isOverlayOpen]);

  useEffect(() => {
    const container = carouselRef.current;
    if (!container || userScrollingRef.current) return;

    if (isOverlayOpen()) {
      return;
    }

    isAutoScrollingRef.current = true;
    const firstItem = container.querySelector(`[data-step="${currentStep}"]`) as HTMLElement;

    if (firstItem) {
      const targetScrollLeft = firstItem.offsetLeft - container.offsetLeft;
      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });
      setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, 600);
    } else {
      isAutoScrollingRef.current = false;
    }
  }, [currentStep, isOverlayOpen]);
  
  const handleScroll = useCallback(() => {
    if (!carouselRef.current) return;

    userScrollingRef.current = true;
    isAutoScrollingRef.current = false;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    const container = carouselRef.current;
    const { scrollLeft, clientWidth: containerWidth } = container;
    const scrollCenter = scrollLeft + containerWidth / 2;

    const items = container.querySelectorAll("[data-step]");
    let bestStep = 0;
    let minDistance = Infinity;

    const stepGroups = new Map<number, HTMLElement[]>();
    items.forEach((item: Element) => {
      const element = item as HTMLElement;
      const stepIndex = parseInt(element.getAttribute("data-step") || "0", 10);
      if (!stepGroups.has(stepIndex)) {
        stepGroups.set(stepIndex, []);
      }
      stepGroups.get(stepIndex)?.push(element);
    });

    stepGroups.forEach((groupItems, stepIndex) => {
      if (groupItems.length > 0) {
        const firstItem = groupItems[0];
        const itemLeft = firstItem.offsetLeft;
        const itemRight = itemLeft + firstItem.offsetWidth;

        if (scrollLeft < itemRight && scrollLeft + containerWidth > itemLeft) {
          const stepCenter = itemLeft + (itemRight - itemLeft) / 2;
          const distance = Math.abs(scrollCenter - stepCenter);

          if (distance < minDistance) {
            minDistance = distance;
            bestStep = stepIndex;
          }
        }
      }
    });

    setCurrentStep((prev) => (bestStep !== prev ? bestStep : prev));

    scrollTimeoutRef.current = window.setTimeout(() => {
      userScrollingRef.current = false;
    }, SCROLL_RESET_TIMEOUT);
  }, []);

  const handleSelectNews = useCallback((item: Blog) => {
    setSelectedNews(item);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedNews(null);
  }, []);

  if (loading && news.length === 0) {
    return (
      <>
        <div className="mb-4 flex items-center justify-between">
          <h6 className="text-base font-medium text-font dark:text-font-dark text-left">
            {translate("news", l)}
          </h6>
          <NavLink
            to={pathNews()}
            className="text-sm text-primary dark:text-primary hover:opacity-80 transition-opacity"
          >
            {translate("showAll", l)}
          </NavLink>
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="w-full mb-2 animate-pulse">
            <div className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark rounded-xl p-4">
              <div className="w-full aspect-video bg-bg-100 dark:bg-bg-100-dark rounded-lg mb-3" />
              <div className="h-4 bg-bg-100 dark:bg-bg-100-dark rounded w-3/4 mb-1" />
              <div className="h-4 bg-bg-100 dark:bg-bg-100-dark rounded w-1/2" />
            </div>
          </div>
        ))}
      </>
    );
  }

  if (news.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h6 className="text-base font-medium text-font dark:text-font-dark text-left">
          {translate("news", l)}
        </h6>
        <NavLink
          to={pathNews()}
          className="text-sm text-primary dark:text-primary hover:opacity-80 transition-opacity"
        >
          {translate("showAll", l)}
        </NavLink>
      </div>

      <div className="news-panel-mobile flex flex-col gap-2">
        {news.map((item, index) => (
          <React.Fragment key={item.id}>
            <NewsItem
              news={item}
              onClick={() => handleSelectNews(item)}
              variant="mobile"
            />
            {index === 0 && (
              <AdSlot
                type={AdPositionEnum.NEWS_ADS}
                priority="adsense"
                className="w-full"
              />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="news-carousel-tablet">
        <div
          ref={carouselRef}
          className="news-carousel flex flex-row gap-4 overflow-x-auto snap-x snap-mandatory -mx-2 px-2"
          onScroll={handleScroll}
        >
          {news.map((item, index) => {
            const stepIndex = Math.floor(index / NEWS_PER_STEP);
            return (
              <React.Fragment key={item.id}>
                <div
                  data-step={stepIndex}
                  className="flex-shrink-0 w-[calc(100vw-4rem)] max-w-sm snap-start h-full"
                >
                  <NewsItem
                    news={item}
                    onClick={() => handleSelectNews(item)}
                    variant="tablet"
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
        {totalSteps > 1 && (
          <div className="pt-1 -mt-1">
            <ProgressIndicator total={totalSteps} current={currentStep} className="py-1" />
          </div>
        )}
      </div>

      <div className="hidden xl:flex flex-col gap-2">
        {news.map((item, index) => (
          <React.Fragment key={item.id}>
            <NewsItem
              news={item}
              onClick={() => handleSelectNews(item)}
              variant="desktop"
            />
            {index === 0 && (
              <AdSlot
                type={AdPositionEnum.NEWS_ADS}
                priority="adsense"
                className="w-full"
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {selectedNews && (
        <NewsDetailModal news={selectedNews} onClose={handleCloseModal} />
      )}
    </>
  );
};

export default NewsPanel;

